import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type BudgetStatusEnum,
  type Maybe,
  QuoteStatus,
} from "@src/__generated__/graphql";
import type { BudgetQuoteModel } from "@src/components/modules/budgets/quotes/budget-quote-model";
import { LinkIconOnHover } from "@src/components/modules/clients/listing/LinkIconOnHover";
import { formatDate, Link, ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { BACK_BUTTON_QUERY_PARAM_KEY } from "@src/components/ui-kit/NavBar/BackButton";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { capitalize } from "lodash";
import { observer } from "mobx-react-lite";
import type { BudgetModel } from "../../projects/budgets/models/budget";

type Props = { row: BudgetModel };
export function BrandRenderer({ row }: Props) {
  return (
    <LinkIconOnHover dataComponentName="view-brand-text">
      <Link
        href={{ pathname: "/brands/detail", query: { id: row.brand?.id } }}
        target="_blank"
      >
        <Text display="flex" flexFlow="row">
          {row.brand?.name ?? row.temporaryBrand?.name ?? ""}
        </Text>
      </Link>
      &nbsp;
    </LinkIconOnHover>
  );
}

export function ProjectRenderer({ row }: Props) {
  return row.project?.id ? (
    <LinkIconOnHover dataComponentName="view-brand-text">
      <Link
        href={{ pathname: "/projects/detail", query: { id: row.project?.id } }}
        target="_blank"
      >
        <Text display="flex" flexFlow="row">
          {row.project?.title}
        </Text>
      </Link>
      &nbsp;
    </LinkIconOnHover>
  ) : (
    <Text>{row.temporaryProject?.name ?? ""}</Text>
  );
}

type BudgetNoRendererProps = {
  budgets: BudgetQuoteModel["budgets"];
};
export function BudgetNoRenderer({ budgets }: BudgetNoRendererProps) {
  return (
    <HStack wrap="wrap" maxW="120px" spacing="0">
      {budgets.map(({ id }, index) => (
        <>
          <Tooltip
            key={`budget-no-${id}`}
            label={t`Edit budget`}
            shouldWrapChildren
          >
            <Link
              href={{
                pathname: "/budgets/proposals",
                query: { budget_id: id },
              }}
              target="_blank"
            >
              {id}
            </Link>
          </Tooltip>
          {index < budgets.length - 1 && <Text>,&nbsp;</Text>}
        </>
      ))}
    </HStack>
  );
}

type StatusRendererProps = {
  status: BudgetStatusEnum;
};
export function StatusRenderer({ status }: StatusRendererProps) {
  return capitalize(status.replace("_", " "));
}

type PriceRendererProps = {
  price: Maybe<number>;
};
export function PriceRenderer({ price }: PriceRendererProps) {
  return currency.formatByWorkspace(price ?? 0);
}

type DateRendererProps = {
  date: Date;
};
export function DateRenderer({ date }: DateRendererProps) {
  return <Text>{formatDate(date)}</Text>;
}

type ActionsRendererProps = {
  quote: BudgetQuoteModel;
};
export const ActionsRenderer = observer(function ActionsRenderer({
  quote,
}: ActionsRendererProps) {
  const confirmDisclosure = useDisclosure();
  const { budgetQuotesListingStore, sendQuoteModalStore } = useStore();

  return (
    <>
      <HStack>
        <Tooltip label={t`Edit`} placement="top" shouldWrapChildren>
          <Link
            href={{
              pathname: "/budgets/edit-quote",
              query: {
                id: quote.id,
                [BACK_BUTTON_QUERY_PARAM_KEY]: "/budgets/quotes",
              },
            }}
          >
            <IconButton
              aria-label={t`Edit button`}
              colorScheme="grey"
              icon={<Icon name="edit-02" />}
              variant="ghost"
            />
          </Link>
        </Tooltip>

        <Tooltip label={t`Delete`} placement="top" shouldWrapChildren>
          <IconButton
            aria-label={t`Delete button`}
            colorScheme="grey"
            icon={<Icon name="trash-03" />}
            isLoading={quote.isDeleting}
            onClick={confirmDisclosure.onOpen}
            variant="ghost"
          />
        </Tooltip>
        <Tooltip label={t`Download`} placement="top" shouldWrapChildren>
          <IconButton
            aria-label={t`Download`}
            colorScheme="grey"
            icon={<Icon name="download-02" />}
            onClick={quote.download}
            variant="ghost"
          />
        </Tooltip>
        <Tooltip label={t`Send`} placement="top" shouldWrapChildren>
          <IconButton
            aria-label={t`Send`}
            colorScheme="grey"
            icon={<Icon name="send-03" />}
            onClick={() =>
              sendQuoteModalStore.modalState.onOpen({
                id: quote.id,
              })
            }
            variant="ghost"
          />
        </Tooltip>
      </HStack>
      <ModalConfirm
        isOpen={confirmDisclosure.isOpen}
        destructive
        onConfirm={async () => {
          await quote.delete();
          budgetQuotesListingStore.fetchBudgetQuotes();
        }}
        onClose={confirmDisclosure.onClose}
      />
    </>
  );
});

const CLIENT_APPROVAL_STATUSES = {
  [QuoteStatus.Draft]: null,
  [QuoteStatus.Pending]: () => t`Pending`,
  [QuoteStatus.Approved]: () => t`Approved`,
  [QuoteStatus.Rejected]: () => t`Rejected`,
  [QuoteStatus.ManualApproved]: () => t`Manually approved`,
};

type ClientApprovalRendererProps = {
  readonly quote: BudgetQuoteModel;
};

export const ClientApprovalRenderer = observer(function ClientApprovalRenderer({
  quote,
}: ClientApprovalRendererProps) {
  const statusText = CLIENT_APPROVAL_STATUSES[quote.status]?.() ?? "";

  return (
    <Box>
      <Text>{statusText}</Text>
      <Text>
        {quote.client_approval_date &&
          formatDate(new Date(quote.client_approval_date))}
      </Text>
      {quote.status !== QuoteStatus.Approved &&
        quote.status !== QuoteStatus.ManualApproved && (
          <Button
            display="none"
            _groupHover={{ display: "block" }}
            colorScheme="grey"
            isLoading={quote.isManualApproving}
            onClick={quote.manualApprove}
            size="xs"
            variant="outline"
          >
            <Trans>Approve manually</Trans>
          </Button>
        )}
    </Box>
  );
});
